import React, { useEffect, useMemo, useRef, useState } from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import {
  Add,
  ArrowForward,
  AssignmentTurnedInOutlined,
  CheckCircleOutline,
  Close,
  EditOutlined,
  ErrorOutline,
  PersonAddAltOutlined,
  ScheduleOutlined,
} from "@mui/icons-material";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { PrimaryButton, SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { globalTextFieldSx } from "components/DesignSystem/sharedUiPatterns";
import { designColors, designRadii, designType, designWeights } from "services/constants/designTokens";
import { panelBodySx } from "pages/PayRoll/PayInputs/Shared/formUi";
import { DateField, SelectField, toOptions } from "pages/PayRoll/PayInputs/Shared/fieldInputs";
import {
  EmptyRow,
  FormField as Field,
  PanelFooter,
  PanelTitleBlock,
  Required,
  SalarySection,
  SimpleTable,
  SummaryStrip,
  ToneChip,
  dangerButtonSx,
  multilineFieldSx,
  searchFieldSx,
  tableCellSx,
  tableHeadSx,
  useSalaryPanelTrail,
} from "../Shared/salaryUi";
import {
  AddComponentPicker,
  CheckLine,
  ComponentCell,
  ValueInput,
  inputSx,
  totalRows,
  visibleCategories,
} from "../SalaryTemplates/SalaryTemplatePanels";
import { CATEGORY_NOUNS, TEMPLATE_CATEGORIES } from "../SalaryTemplates/salaryTemplateData";
import {
  TEMPLATE_METHODS,
  codesIn,
  compareLines,
  lineCategory,
  lineErrors,
  lineFromComponent,
  lineName,
} from "../SalaryTemplates/salaryTemplateData";
import { baseCodes } from "../SalaryComponents/salaryComponentData";
import {
  APPROVAL_LEVELS,
  PERIOD,
  SALARY_TYPES,
  checkAssignForm,
  customizedCount,
  employeeById,
  employeeCount,
  employeeLines,
  evaluateTemplate,
  formatDay,
  formatRupees,
  formatStamp,
  requestStatus,
  requestTemplate,
  requestTemplateVersion,
  scaleLinesToGross,
  grossForLines,
  defaultEffectiveFrom,
  structureChanges,
  totalGross,
  versionLabel,
  withBalancing,
} from "./assignSalaryData";

const SCREEN = "Assign Salary";
const money = (value) => (value == null || Number.isNaN(Number(value)) ? "–" : formatRupees(value));

// ---------------- Shared bits ----------------

export const BreakdownList = ({ catalog, lines, result }) => {
  const shown = withBalancing(catalog, lines);
  const group = (category) => shown.filter((line) => lineCategory(catalog, line) === category);
  const row = (label, value, strong) => (
    <Mui.Stack key={label} direction="row" justifyContent="space-between" sx={{ py: 0.6 }}>
      <Mui.Typography sx={{ fontSize: 14, color: designColors.slate700, fontWeight: strong ? designWeights.heading : 400 }}>{label}</Mui.Typography>
      <Mui.Typography sx={{ fontSize: 14, color: designColors.slate900, fontWeight: strong ? designWeights.heading : 400 }}>{money(value)}</Mui.Typography>
    </Mui.Stack>
  );
  const heading = (label) => (
    <Mui.Typography sx={{ ...designType.captionText, mt: 1.25, mb: 0.25, letterSpacing: "0.04em", fontWeight: designWeights.semibold }}>{label}</Mui.Typography>
  );
  const divider = <Mui.Box sx={{ borderTop: `1px solid ${designColors.slate200}`, my: 0.5 }} />;
  return (
    <Mui.Box>
      {TEMPLATE_CATEGORIES.map((category) =>
        category === "Earnings" || group(category).length ? (
          <React.Fragment key={category}>
            {heading(`${CATEGORY_NOUNS[category][1].toUpperCase()}${category === "Calculation" ? " · NOT PAID" : ""}`)}
            {group(category).map((line) => row(lineName(catalog, line), result.values[line.code]))}
          </React.Fragment>
        ) : null,
      )}
      {divider}
      {totalRows(catalog, shown).map(([label, key, strong]) => row(label, result[key], strong))}
    </Mui.Box>
  );
};

// customized: the server says so even when the template rows to compare
// against aren't loaded.
export const StructureChip = ({ changes, customized, scaled }) =>
  scaled ? (
    <ToneChip label="Scaled to gross" tone="purple" />
  ) : changes ? (
    <ToneChip label={`Customized · ${changes} change${changes === 1 ? "" : "s"}`} tone="purple" />
  ) : customized ? (
    <ToneChip label="Customized" tone="purple" />
  ) : (
    <ToneChip label="Template Linked" tone="slate" />
  );

// L1 -> L2 -> Effective boxes.
export const ApprovalSteps = ({ request }) => {
  const steps = APPROVAL_LEVELS.map((item) => {
    const approval = request?.history?.find((entry) => entry.title === `Approved at L${item.level}`);
    const rejectedAt = request?.status === "Rejected" ? request.rejection?.level ?? request.level : null;
    let state = "waiting";
    if (approval || request?.status === "Approved") state = "done";
    else if (rejectedAt === item.level) state = "rejected";
    else if (rejectedAt && rejectedAt > item.level) state = "done";
    else if (request?.status === "Pending" && request.level === item.level) state = "current";
    else if (request?.status === "Pending" && request.level > item.level) state = "done";
    const who = (value) => String(value || "").split(" (")[0];
    const detail =
      state === "done"
        ? approval && who(approval.by)
          ? `Approved by ${who(approval.by)}${approval.at ? ` · ${moment(approval.at).format("DD MMM")}` : ""}`
          : "Approved"
        : state === "current" ? "Waiting for approval"
          : state === "rejected" ? `Rejected${who(request.rejection?.by) ? ` by ${who(request.rejection.by)}` : ""}`
            : "Not reached yet";
    return { ...item, state, detail };
  });
  const colors = {
    done: { border: "#A7E3CB", bg: "#ECFDF3", icon: <CheckCircleOutline sx={{ fontSize: 20, color: "#067647" }} /> },
    current: { border: "#F2D680", bg: "#FFFBEB", icon: <ScheduleOutlined sx={{ fontSize: 20, color: "#B54708" }} /> },
    rejected: { border: "#FCA5A5", bg: "#FEF2F2", icon: <ErrorOutline sx={{ fontSize: 20, color: "#B42318" }} /> },
    waiting: { border: designColors.slate200, bg: designColors.white, icon: null },
  };
  const box = (key, title, detail, state) => (
    <Mui.Stack key={key} direction="row" spacing={1} alignItems="center" sx={{ border: `1px solid ${colors[state].border}`, backgroundColor: colors[state].bg, borderRadius: designRadii.card, px: 1.5, py: 1 }}>
      {colors[state].icon}
      <Mui.Box>
        <Mui.Typography sx={{ fontSize: 14, fontWeight: designWeights.semibold, color: designColors.slate900 }}>{title}</Mui.Typography>
        {detail ? <Mui.Typography sx={designType.captionText}>{detail}</Mui.Typography> : null}
      </Mui.Box>
    </Mui.Stack>
  );
  return (
    <Mui.Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
      {steps.map((step) => (
        <React.Fragment key={step.level}>
          {box(step.level, `L${step.level} · ${step.role}`, request ? step.detail : null, step.state)}
          <ArrowForward sx={{ fontSize: 16, color: designColors.slate400 }} />
        </React.Fragment>
      ))}
      {box("effective", "Effective", request ? "From each employee's date" : null, request?.status === "Approved" ? "done" : "waiting")}
    </Mui.Stack>
  );
};

// ---------------- Customize structure ----------------

// fullWidth opens it as the full view (Assign Salary) instead of a side peek.
// grossFollowsEarnings: changed amounts move the gross to the new Earnings
// total (Assign) instead of being refused; Revision's common structure keeps
// its sample gross.
export const CustomizeStructurePanel = ({ open, catalog, employee, template, version, row, onClose, onApply, fullWidth = false, grossFollowsEarnings = false }) => {
  const trail = useSalaryPanelTrail(SCREEN, onClose);
  const [lines, setLines] = useState([]);
  const [showErrors, setShowErrors] = useState(false);
  const baseLines = useMemo(() => version?.lines || [], [version]);
  useEffect(() => {
    if (!open || !row) return;
    setLines((row.lines || baseLines).map((line) => ({ ...line })));
    setShowErrors(false);
  }, [open, row, baseLines]);
  if (!row || !employee || !template) return null;

  const rowGross = Number(row.gross) > 0 ? Number(row.gross) : 0;
  const errors = lineErrors(catalog, lines);
  const followed = grossFollowsEarnings && !Object.keys(errors).length ? grossForLines(catalog, lines, rowGross) : null;
  const gross = followed ?? rowGross;
  const grossChanged = Math.abs(gross - rowGross) >= 0.01;
  const shown = withBalancing(catalog, lines);
  const result = evaluateTemplate(catalog, lines, gross);
  const formulasOk = !Object.keys(errors).length && !result.unresolved.length;
  const changes = compareLines(catalog, baseLines, lines).filter((item) => item.change !== "Unchanged");
  const initial = row.lines || baseLines;
  const dirty = JSON.stringify(compareLines(catalog, initial, lines).filter((item) => item.change !== "Unchanged")) !== "[]";
  const period = PERIOD[template.pay_basis];
  const usedBy = (code) =>
    shown.filter((line) => line.code !== code && !line.balancing && (baseCodes(line.base).includes(code) || codesIn(line.formula).includes(code))).map((line) => line.code);
  const update = (code, change) => setLines((current) => current.map((line) => (line.code === code ? { ...line, ...change } : line)));
  const add = (code) => {
    const added = lineFromComponent(catalog, code);
    const balancingIndex = lines.findIndex((line) => line.balancing);
    setLines(
      lineCategory(catalog, added) === "Earnings" && balancingIndex >= 0
        ? [...lines.slice(0, balancingIndex), added, ...lines.slice(balancingIndex)]
        : [...lines, added],
    );
  };
  const apply = () => {
    setShowErrors(true);
    if (!formulasOk || !result.balanced) return;
    onApply(changes.length ? lines : null, gross);
  };

  const table = (category) => {
    const rows = shown.filter((line) => lineCategory(catalog, line) === category);
    return (
      <>
        <Mui.TableRow sx={{ backgroundColor: designColors.slateBg }}>
          <Mui.TableCell colSpan={5} sx={{ ...tableHeadSx, fontSize: 14 }}>{category}</Mui.TableCell>
        </Mui.TableRow>
        <Mui.TableRow>
          {["Component", "Method", "Value / formula", "Amount", ""].map((head, index) => (
            <Mui.TableCell key={head || index} align={index === 3 ? "right" : "left"} sx={{ ...tableHeadSx, backgroundColor: designColors.white, width: [undefined, 150, 230, 100, 40][index] }}>{head}</Mui.TableCell>
          ))}
        </Mui.TableRow>
        {rows.map((line) => {
          const dependents = usedBy(line.code);
          const added = !baseLines.some((item) => item.code === line.code);
          const error = showErrors ? errors[line.code] : undefined;
          return (
            <Mui.TableRow key={line.code} sx={{ backgroundColor: added ? "#ECFDF3" : undefined }}>
              <Mui.TableCell sx={tableCellSx}>
                <ComponentCell catalog={catalog} line={line} tags={added ? <ToneChip label="Added" tone="green" /> : null} />
                {dependents.length ? <Mui.Typography sx={designType.captionText}>Used by {dependents.join(", ")}, can't remove</Mui.Typography> : null}
              </Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>
                <Mui.TextField size="small" select value={line.method} disabled={line.balancing} onChange={(event) => update(line.code, { method: event.target.value, amount: "", percentage: "", base: "GROSS", formula: "" })} inputProps={{ "aria-label": `${line.code} method` }} sx={inputSx}>
                  {TEMPLATE_METHODS.map((method) => <Mui.MenuItem key={method} value={method}>{method}</Mui.MenuItem>)}
                </Mui.TextField>
              </Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>
                <ValueInput catalog={catalog} lines={shown} line={line} error={error} onChange={(change) => update(line.code, change)} />
                {error ? <Mui.Typography sx={{ ...designType.captionText, color: "#DC2626", mt: 0.5 }}>{error}</Mui.Typography> : null}
              </Mui.TableCell>
              <Mui.TableCell align="right" sx={{ ...tableCellSx, fontWeight: designWeights.semibold }}>{money(result.values[line.code])}</Mui.TableCell>
              <Mui.TableCell sx={{ ...tableCellSx, px: 0.5 }}>
                <Mui.IconButton size="small" disabled={dependents.length > 0} aria-label={`Remove ${lineName(catalog, line)}`} onClick={() => setLines((current) => current.filter((item) => item.code !== line.code))}>
                  <Close fontSize="small" />
                </Mui.IconButton>
              </Mui.TableCell>
            </Mui.TableRow>
          );
        })}
        <Mui.TableRow>
          <Mui.TableCell colSpan={5} sx={tableCellSx}>
            <AddComponentPicker catalog={catalog} category={category} lines={lines} payBasis={template.pay_basis} onAdd={add} />
          </Mui.TableCell>
        </Mui.TableRow>
      </>
    );
  };

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      {...(fullWidth ? { expanded: Boolean(open), onToggleExpand: () => {}, hideExpand: true, expandedZIndex: 1300 } : {})}
      breadcrumb={[...trail, "Customize structure"]}
      header={
        <PanelTitleBlock
          icon={<EditOutlined />}
          title={`Customize structure · ${employee.name}`}
          subtitle={`${employee.code} · Based on ${template.name} ${versionLabel(version.version)} · Gross ${money(gross)} / ${period}`}
        />
      }
      bodySx={panelBodySx}
      footer={
        <PanelFooter note="Name, code and category come from the component">
          <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
          <PrimaryButton onClick={apply} disabled={!dirty}>Apply to {employee.name}</PrimaryButton>
        </PanelFooter>
      }
    >
      <Mui.Stack spacing={2}>
        <RequestInfoBanner
          body={<>Changes apply <b>only to {employee.name}</b>. The template stays the same. {employee.name} will show as <b>Customized</b>, and approvers see every change.</>}
        />
        <Mui.Box sx={{ border: `1px solid ${designColors.slate200}`, borderRadius: designRadii.card, overflowX: "auto", backgroundColor: designColors.white }}>
          <Mui.Table size="small" sx={{ minWidth: 620 }}>
            <Mui.TableBody>
              {visibleCategories(catalog, lines).map((category) => <React.Fragment key={category}>{table(category)}</React.Fragment>)}
              {totalRows(catalog, lines).map(([label, key, strong]) =>
                strong ? (
                  <Mui.TableRow key={label} sx={{ backgroundColor: designColors.slateBg }}>
                    <Mui.TableCell colSpan={3} sx={{ ...tableCellSx, fontSize: 16, fontWeight: designWeights.heading }}>{label}</Mui.TableCell>
                    <Mui.TableCell colSpan={2} align="right" sx={{ ...tableCellSx, fontSize: 16, fontWeight: designWeights.heading }}>{money(result[key])}</Mui.TableCell>
                  </Mui.TableRow>
                ) : (
                  <Mui.TableRow key={label}>
                    <Mui.TableCell colSpan={3} sx={tableCellSx}>{label}</Mui.TableCell>
                    <Mui.TableCell colSpan={2} align="right" sx={tableCellSx}>{money(result[key])}</Mui.TableCell>
                  </Mui.TableRow>
                ),
              )}
            </Mui.TableBody>
          </Mui.Table>
        </Mui.Box>
        <CheckLine ok={result.balanced && formulasOk}>
          {result.balanced && formulasOk
            ? grossChanged
              ? `Gross / ${period} updates to ${money(gross)} (was ${money(rowGross)}) · formulas valid`
              : `Earnings add up to gross (${money(gross)}) · formulas valid`
            : !formulasOk
              ? "Fix the highlighted values"
              : `Earnings add up to ${money(result.earnings)}, not gross (${money(gross)})`}
        </CheckLine>
        {changes.length ? (
          <SalarySection
            title={`${changes.length} change${changes.length === 1 ? "" : "s"} from template`}
            tone="warning"
            action={
              <Mui.Link component="button" type="button" underline="hover" onClick={() => setLines(baseLines.map((line) => ({ ...line })))} sx={{ fontSize: 13, fontWeight: designWeights.semibold, color: designColors.tealDark }}>
                Reset to template
              </Mui.Link>
            }
          >
            <Mui.Box component="ul" sx={{ m: 0, pl: 2.5, "& li": { fontSize: 14, color: designColors.slate700, mb: 0.5 } }}>
              {changes.map((item) => (
                <li key={item.code}>
                  <b>{item.change}</b> {item.name}
                  {item.change === "Added" ? ` · ${item.after}` : item.change === "Removed" ? "" : ` · ${item.before} → ${item.after}`}
                </li>
              ))}
            </Mui.Box>
          </SalarySection>
        ) : (
          <RequestInfoBanner body={<>No changes yet. Every row matches <b>{template.name} {versionLabel(version.version)}</b>. Add, remove or change a row to customize.</>} />
        )}
      </Mui.Stack>
    </GlobalSidePanel>
  );
};

// ---------------- Assign form ----------------

// Employees with no salary (assign-not-configured), minus the ones already
// on the form. The selected payroll group's employees come first.
const AddEmployeesPicker = ({ available, error, form, groupName, onAdd }) => {
  const [anchor, setAnchor] = useState(null);
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState([]);
  const query = search.trim().toLowerCase();
  const onForm = new Set(form.employees.map((row) => String(row.employee_id)));
  const options = available
    .filter((employee) => !onForm.has(String(employee.id)))
    .filter((employee) => !query || `${employee.name} ${employee.code}`.toLowerCase().includes(query))
    .sort((a, b) => (groupName && a.group === groupName ? 0 : 1) - (groupName && b.group === groupName ? 0 : 1) || a.name.localeCompare(b.name));
  return (
    <>
      <Mui.Button
        startIcon={<Add />}
        onClick={(event) => { setSearch(""); setPicked([]); setAnchor(event.currentTarget); }}
        sx={{ textTransform: "none", fontWeight: designWeights.semibold, color: designColors.tealDark, border: `1px dashed ${designColors.slate300}`, borderRadius: designRadii.card, px: 2, flexShrink: 0 }}
      >
        Add employees
      </Mui.Button>
      <Mui.Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{ sx: { width: 420, maxWidth: "calc(100vw - 32px)", mt: 0.5, borderRadius: designRadii.card } }}
      >
        <Mui.Box sx={{ p: 1.5 }}>
          <Mui.TextField size="small" autoFocus value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search name or code" sx={{ ...searchFieldSx, width: "100%" }} />
        </Mui.Box>
        <Mui.Box sx={{ maxHeight: 300, overflowY: "auto" }}>
          {options.length ? (
            options.map((employee) => (
              <Mui.FormControlLabel
                key={employee.id}
                sx={{ m: 0, width: "100%", px: 1, py: 0.5, borderTop: `1px solid ${designColors.slate100}` }}
                control={
                  <Mui.Checkbox
                    size="small"
                    checked={picked.includes(employee.id)}
                    onChange={() => setPicked((current) => (current.includes(employee.id) ? current.filter((id) => id !== employee.id) : [...current, employee.id]))}
                    sx={{ "&.Mui-checked": { color: designColors.tealDark } }}
                  />
                }
                label={
                  <Mui.Box>
                    <Mui.Typography sx={{ fontSize: 14, fontWeight: designWeights.semibold }}>{employee.name}</Mui.Typography>
                    <Mui.Typography sx={designType.captionText}>
                      {[employee.code, employee.department, employee.group].filter(Boolean).join(" · ")}
                    </Mui.Typography>
                  </Mui.Box>
                }
              />
            ))
          ) : (
            <Mui.Typography sx={{ ...designType.helperText, px: 2, py: 1.5, color: error ? "#B42318" : undefined }}>
              {error
                ? `Couldn't load the employees with no salary: ${error}`
                : query
                  ? "No employee matches this search."
                  : available.length
                    ? "Everyone with no salary is already on this form."
                    : "No employees without a salary. Everyone active has one already or is in another open request."}
            </Mui.Typography>
          )}
        </Mui.Box>
        <Mui.Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2, py: 1.25, borderTop: `1px solid ${designColors.slate200}`, backgroundColor: designColors.slateBg }}>
          <Mui.Typography sx={designType.captionText}>Active employees with no salary</Mui.Typography>
          <PrimaryButton disabled={!picked.length} onClick={() => { onAdd(picked); setAnchor(null); }} sx={{ minWidth: 0, minHeight: 36, px: 2 }}>
            Add {picked.length || ""}
          </PrimaryButton>
        </Mui.Stack>
      </Mui.Popover>
    </>
  );
};

const deleteButtonSx = { color: "#B42318", borderColor: "#FCA5A5", "&:hover": { borderColor: "#B42318", backgroundColor: "#FEF2F2" } };

export const AssignSalaryFormPanel = ({
  open,
  initial,
  request,
  catalog,
  templates,
  people,
  available,
  availableError,
  choicesByType,
  busy,
  expanded,
  onToggleExpand,
  onClose,
  onSalaryType,
  onTemplate,
  onSaveDraft,
  onSubmit,
  onDelete,
}) => {
  const trail = useSalaryPanelTrail(SCREEN, onClose, busy);
  const [form, setForm] = useState(initial);
  const [previewId, setPreviewId] = useState(null);
  const [showErrors, setShowErrors] = useState(false);
  const [customizeId, setCustomizeId] = useState(null);
  useEffect(() => {
    if (!open) return;
    setForm(initial);
    setShowErrors(false);
    setCustomizeId(null);
    setPreviewId(initial?.employees[0]?.employee_id ?? null);
  }, [open, initial]);
  // Templates and groups for the salary type; the template's rows for the
  // preview, the checks and Customize.
  useEffect(() => {
    if (open && form?.salary_type) onSalaryType(form.salary_type);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form?.salary_type]);
  useEffect(() => {
    if (open && form?.template_id) onTemplate(form.template_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form?.template_id]);

  const template = form ? templates.find((item) => String(item.id) === String(form.template_id)) || null : null;
  const check = useMemo(
    () => (form ? checkAssignForm(form, { catalog, template, employees: people }) : null),
    [form, catalog, template, people],
  );
  // The selected template's salary (salary-template/?template_id=) is the
  // default gross. It fills empty boxes and boxes still holding the previous
  // template's default; amounts the user typed are kept.
  const templateGross = Number(template?.preview_gross) > 0 ? String(template.preview_gross) : "";
  const autoGross = useRef("");
  useEffect(() => {
    if (!open) autoGross.current = "";
  }, [open]);
  useEffect(() => {
    if (!open || !templateGross) return;
    const previous = autoGross.current;
    autoGross.current = templateGross;
    const replace = (value) => value === "" || value == null || (previous !== "" && String(value) === previous);
    setForm((current) => {
      if (!current) return current;
      const sameGross = replace(current.same_gross) ? templateGross : current.same_gross;
      const employees = current.employees.map((row) =>
        current.gross_mode === "same" ? { ...row, gross: sameGross } : replace(row.gross) ? { ...row, gross: templateGross } : row,
      );
      return { ...current, same_gross: sameGross, employees };
    });
  }, [open, templateGross]);
  // A gross the template's earnings don't add up to (30,000 template, 40,000
  // typed): its fixed earnings are scaled to the new gross, the same as
  // retyping them in Customize. Rows customized by hand are left alone.
  const baseLines = check?.linesReady ? check.version.lines : null;
  const grossKey = form ? form.employees.map((row) => `${row.employee_id}:${row.gross}:${row.lines ? (row.auto_lines ? "a" : "m") : "-"}`).join("|") : "";
  useEffect(() => {
    if (!open || !baseLines) return;
    setForm((current) => {
      if (!current) return current;
      let changed = false;
      const employees = current.employees.map((row) => {
        if (row.lines && !row.auto_lines) return row;
        const scaled = scaleLinesToGross(catalog, baseLines, Number(row.gross));
        if (scaled) {
          if (row.auto_lines && JSON.stringify(row.lines) === JSON.stringify(scaled)) return row;
          changed = true;
          return { ...row, lines: scaled, auto_lines: true };
        }
        if (!row.auto_lines) return row;
        changed = true;
        return { ...row, lines: null, auto_lines: false };
      });
      return changed ? { ...current, employees } : current;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, baseLines, catalog, grossKey]);
  if (!form || !check) return null;

  const { version } = check;
  const choices = choicesByType?.[form.salary_type] || null;
  const period = PERIOD[form.salary_type] || "month";
  const groups = choices?.groups || [];
  const group = groups.find((item) => String(item.id) === String(form.payroll_group_id));
  const groupOptions = groups.map((item) => ({ value: item.id, label: item.name }));
  if (form.payroll_group_id && !group && request?.payroll_group) groupOptions.push({ value: form.payroll_group_id, label: request.payroll_group });
  const templateOptions = (choices?.templates || [])
    .filter((item) => !item.pay_basis || item.pay_basis === form.salary_type)
    .map((item) => ({ value: item.id, label: `${item.name} · ${versionLabel(item.version)}` }));
  if (form.template_id && !templateOptions.some((item) => String(item.value) === String(form.template_id))) {
    const name = template?.name || request?.template_name;
    if (name) templateOptions.push({ value: form.template_id, label: `${name} · ${versionLabel(request?.template_version ?? version?.version ?? 1)}` });
  }
  const salaryTypes = choices?.salaryTypes || SALARY_TYPES;
  const set = (change) => setForm((current) => ({ ...current, ...change }));
  const setRow = (id, change) => set({ employees: form.employees.map((row) => (String(row.employee_id) === String(id) ? { ...row, ...change } : row)) });
  const changesFor = (row) => (row.lines && check.linesReady ? compareLines(catalog, version.lines, row.lines).filter((item) => item.change !== "Unchanged").length : 0);
  const customized = form.employees.filter((row) => row.lines && (changesFor(row) || !check.linesReady));
  const previewRow = form.employees.find((row) => String(row.employee_id) === String(previewId)) || form.employees[0];
  const previewResult = previewRow && check.linesReady && Number(previewRow.gross) > 0 ? evaluateTemplate(catalog, previewRow.lines || version.lines, Number(previewRow.gross)) : null;
  const customizeRow = form.employees.find((row) => String(row.employee_id) === String(customizeId));
  const blocking = Object.keys(check.errors).length || Object.keys(check.perEmployee).length;
  const count = form.employees.length;
  const person = (id) => employeeById(people, id) || { id, name: `Employee ${id}`, code: "" };
  const allGross = form.employees.every((row) => Number(row.gross) > 0);

  const submit = () => {
    setShowErrors(true);
    if (blocking) return;
    onSubmit(form);
  };
  const saveDraft = () => {
    setShowErrors(true);
    if (check.errors.template || check.errors.payroll_group) return;
    onSaveDraft(form);
  };

  const setup = (
    <SalarySection title="Salary setup" action={<Mui.Typography sx={designType.captionText}>Applies to everyone below</Mui.Typography>}>
      <Mui.Stack spacing={2}>
        <Field label={<>Payroll group<Required /></>} helper="Add employees lists this group first" error={showErrors ? check.errors.payroll_group : undefined}>
          <SelectField value={form.payroll_group_id} onChange={(value) => set({ payroll_group_id: value || null })} options={groupOptions} placeholder="Select payroll group" disabled={busy} />
        </Field>
        <Field label="Pay cycle" helper="From the payroll group">
          <Mui.TextField size="small" value={group?.cycle || (form.payroll_group_id && request?.pay_cycle) || "–"} disabled sx={multilineFieldSx} />
        </Field>
        <Field label={<>Salary type<Required /></>} helper="The template's pay basis must match">
          <SelectField
            value={form.salary_type}
            onChange={(value) => {
              if (!value) return;
              set({ salary_type: value, template_id: null, employees: form.employees.map((row) => ({ ...row, lines: null })) });
            }}
            options={toOptions(salaryTypes)}
            placeholder="Select salary type"
            disabled={busy}
          />
        </Field>
        <Field label={<>Template<Required /></>} helper={`Only ${form.salary_type} templates are listed`} error={showErrors ? check.errors.template : undefined}>
          <SelectField
            value={form.template_id}
            onChange={(value) => set({ template_id: value || null, employees: form.employees.map((row) => ({ ...row, lines: null })) })}
            options={templateOptions}
            placeholder={choices ? "Select template" : "Loading templates..."}
            disabled={busy}
          />
        </Field>
        <Field label={<>Comments <Mui.Box component="span" sx={{ color: designColors.slate500 }}>· optional</Mui.Box></>} helper="Shown to the approvers">
          <Mui.TextField
            size="small"
            value={form.comments}
            onChange={(event) => set({ comments: event.target.value })}
            multiline
            minRows={2}
            placeholder="e.g. October joiners"
            disabled={busy}
            sx={multilineFieldSx}
          />
        </Field>
      </Mui.Stack>
    </SalarySection>
  );

  const employeesSection = (
    <SalarySection
      title={`Employees & gross · ${count}`}
      action={
        <Mui.ToggleButtonGroup
          size="small"
          exclusive
          value={form.gross_mode}
          onChange={(event, value) => value && set({ gross_mode: value })}
          sx={{ "& .MuiToggleButton-root": { textTransform: "none", px: 1.5, py: 0.25, fontSize: 13 }, "& .Mui-selected": { color: `${designColors.tealDark} !important`, backgroundColor: `${designColors.tealTint} !important` } }}
        >
          <Mui.ToggleButton value="same">Same gross for all</Mui.ToggleButton>
          <Mui.ToggleButton value="per">Per employee</Mui.ToggleButton>
        </Mui.ToggleButtonGroup>
      }
    >
      <Mui.Stack spacing={1.5}>
        {form.gross_mode === "same" ? (
          <Mui.Stack direction="row" spacing={1.5} alignItems="center">
            <Mui.Typography sx={{ fontSize: 14, color: designColors.slate700 }}>Gross / {period} for everyone</Mui.Typography>
            <Mui.TextField
              size="small"
              type="number"
              value={form.same_gross}
              onChange={(event) => set({ same_gross: event.target.value, employees: form.employees.map((row) => ({ ...row, gross: event.target.value })) })}
              InputProps={{ startAdornment: <Mui.InputAdornment position="start">₹</Mui.InputAdornment> }}
              inputProps={{ "aria-label": "Same gross for all", min: 0 }}
              sx={{ ...globalTextFieldSx, width: 180 }}
            />
          </Mui.Stack>
        ) : null}
        <Mui.Box sx={{ mx: -2, overflowX: "auto" }}>
          <Mui.Table size="small" sx={{ minWidth: 760 }}>
            <Mui.TableHead>
              <Mui.TableRow sx={{ backgroundColor: designColors.slateBg }}>
                {["Employee", `Gross / ${period}`, "Effective from", "Structure", "", ""].map((head, index) => (
                  <Mui.TableCell key={`${head}-${index}`} sx={{ ...tableHeadSx, width: [undefined, 160, 180, 170, 140, 44][index] }}>{head}</Mui.TableCell>
                ))}
              </Mui.TableRow>
            </Mui.TableHead>
            <Mui.TableBody>
              {form.employees.map((row) => {
                const employee = person(row.employee_id);
                const changes = changesFor(row);
                const problems = showErrors ? check.perEmployee[row.employee_id] : null;
                const selected = previewRow && String(previewRow.employee_id) === String(row.employee_id);
                return (
                  <Mui.TableRow key={row.employee_id} onClick={() => setPreviewId(row.employee_id)} sx={{ cursor: "pointer", backgroundColor: selected ? designColors.tealTint : undefined, boxShadow: selected ? `inset 3px 0 0 ${designColors.teal}` : "none" }}>
                    <Mui.TableCell sx={{ ...tableCellSx, verticalAlign: "top" }}>
                      <Mui.Typography sx={{ fontSize: 14, fontWeight: designWeights.semibold, color: designColors.slate900 }}>{employee.name}</Mui.Typography>
                      <Mui.Typography sx={designType.captionText}>{[employee.code, employee.department, employee.position].filter(Boolean).join(" · ")}</Mui.Typography>
                      {problems ? <Mui.Typography sx={{ ...designType.captionText, color: "#DC2626", mt: 0.25 }}>{problems.join(" · ")}</Mui.Typography> : null}
                    </Mui.TableCell>
                    <Mui.TableCell sx={{ ...tableCellSx, verticalAlign: "top" }} onClick={(event) => event.stopPropagation()}>
                      <Mui.TextField
                        size="small"
                        type="number"
                        value={row.gross}
                        disabled={form.gross_mode === "same" || busy}
                        onChange={(event) => setRow(row.employee_id, { gross: event.target.value })}
                        InputProps={{ startAdornment: <Mui.InputAdornment position="start">₹</Mui.InputAdornment> }}
                        inputProps={{ "aria-label": `${employee.name} gross`, min: 0, style: { textAlign: "right" } }}
                        sx={inputSx}
                      />
                    </Mui.TableCell>
                    <Mui.TableCell sx={{ ...tableCellSx, verticalAlign: "top" }} onClick={(event) => event.stopPropagation()}>
                      <DateField
                        value={row.effective_from}
                        onChange={(value) => setRow(row.employee_id, { effective_from: value })}
                        minDate={employee.joined || undefined}
                        disabled={busy}
                        error={problems?.some((text) => /effective date|before joining/i.test(text))}
                      />
                      {employee.joined ? <Mui.Typography sx={{ ...designType.captionText, mt: 0.5 }}>Joined {formatDay(employee.joined)}</Mui.Typography> : null}
                    </Mui.TableCell>
                    <Mui.TableCell sx={{ ...tableCellSx, verticalAlign: "top", pt: "18px" }}><StructureChip changes={changes} customized={Boolean(row.lines)} scaled={Boolean(row.lines && row.auto_lines)} /></Mui.TableCell>
                    <Mui.TableCell sx={{ ...tableCellSx, verticalAlign: "top", pt: "19px" }} onClick={(event) => event.stopPropagation()}>
                      <Mui.Link component="button" type="button" underline="hover" disabled={busy || !check.linesReady || !(Number(row.gross) > 0)} onClick={() => setCustomizeId(row.employee_id)} sx={{ fontSize: 14, fontWeight: designWeights.semibold, whiteSpace: "nowrap", color: designColors.tealDark, "&:disabled": { color: designColors.slate400, cursor: "default" } }}>
                        {row.lines ? "Edit structure" : "Customize"}
                      </Mui.Link>
                    </Mui.TableCell>
                    <Mui.TableCell sx={{ ...tableCellSx, verticalAlign: "top", pt: "14px", px: 0.5 }} onClick={(event) => event.stopPropagation()}>
                      <Mui.IconButton size="small" disabled={busy} aria-label={`Remove ${employee.name}`} onClick={() => set({ employees: form.employees.filter((item) => String(item.employee_id) !== String(row.employee_id)) })}>
                        <Close fontSize="small" />
                      </Mui.IconButton>
                    </Mui.TableCell>
                  </Mui.TableRow>
                );
              })}
              {!count ? <EmptyRow colSpan={6} text="No employees yet. Add the employees who need a first salary." /> : null}
            </Mui.TableBody>
          </Mui.Table>
        </Mui.Box>
        <Mui.Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
          <AddEmployeesPicker
            available={available}
            error={availableError}
            form={form}
            groupName={group?.name}
            onAdd={(ids) =>
              set({
                employees: [
                  ...form.employees,
                  ...ids.map((id) => ({ employee_id: id, gross: form.gross_mode === "same" ? form.same_gross : templateGross, effective_from: defaultEffectiveFrom(person(id).joined), lines: null })),
                ],
              })
            }
          />
          <Mui.Typography sx={designType.captionText}>
            Effective from defaults to the joining date. It can be changed, but not to a date before joining or before the first unprocessed payroll.
          </Mui.Typography>
        </Mui.Stack>
        {showErrors && check.errors.employees ? <Mui.Typography sx={{ ...designType.captionText, color: "#DC2626" }}>{check.errors.employees}</Mui.Typography> : null}
      </Mui.Stack>
    </SalarySection>
  );

  const previewOptions = form.employees.map((row) => ({
    value: String(row.employee_id),
    label: `${person(row.employee_id).name} · ${Number(row.gross) > 0 ? money(row.gross) : "no gross"}`,
  }));

  const side = (
    <Mui.Stack spacing={2}>
      <SalarySection
        title="Preview"
        action={
          previewOptions.length ? (
            <Mui.TextField size="small" select value={previewRow ? String(previewRow.employee_id) : ""} onChange={(event) => setPreviewId(event.target.value)} inputProps={{ "aria-label": "Preview employee" }} sx={{ ...inputSx, width: 220 }}>
              {previewOptions.map((option) => <Mui.MenuItem key={option.value} value={option.value}>{option.label}</Mui.MenuItem>)}
            </Mui.TextField>
          ) : null
        }
      >
        {previewResult ? (
          <BreakdownList catalog={catalog} lines={previewRow.lines || version.lines} result={previewResult} />
        ) : (
          <Mui.Typography sx={designType.helperText}>
            {form.template_id && !check.linesReady ? "Loading the template's components..." : "Pick a template and enter a gross to see the breakdown."}
          </Mui.Typography>
        )}
        <Mui.Box sx={{ mt: 1.5 }}>
          <RequestInfoBanner body="PF, ESI and PT are added at payroll, based on each employee's eligibility in Statutory." />
        </Mui.Box>
      </SalarySection>
      {count ? (
        <>
          <CheckLine ok>{`${count} employee${count === 1 ? "" : "s"} · active, no salary yet`}</CheckLine>
          {check.linesReady ? (
            <CheckLine ok={check.earningsOk && allGross}>
              {check.earningsOk && allGross ? `Earnings add up to gross for all ${count}` : "Enter a gross for everyone; earnings must add up to it"}
            </CheckLine>
          ) : null}
          {customized.map((row) => (
            <Mui.Stack key={row.employee_id} direction="row" spacing={0.75} alignItems="flex-start" sx={{ color: "#B54708" }}>
              <ErrorOutline sx={{ fontSize: 18, mt: 0.1 }} />
              <Mui.Typography sx={{ fontSize: 14, fontWeight: designWeights.semibold }}>
                {row.auto_lines
                  ? `${person(row.employee_id).name}'s fixed earnings were scaled to the new gross. Approvers will see the change.`
                  : `${person(row.employee_id).name} has a customized structure. Approvers will see the change.`}
              </Mui.Typography>
            </Mui.Stack>
          ))}
        </>
      ) : null}
      <SalarySection title="Approval route">
        <ApprovalSteps request={null} />
        <Mui.Typography sx={{ ...designType.captionText, mt: 1.5 }}>
          Sent as one request for {count} employee{count === 1 ? "" : "s"}. Nothing changes until L2 approves.
        </Mui.Typography>
      </SalarySection>
    </Mui.Stack>
  );

  return (
    <>
      <GlobalSidePanel
        open={open}
        onClose={busy ? undefined : onClose}
        expanded={expanded}
        onToggleExpand={onToggleExpand}
        breadcrumb={[...trail, request ? request.number : "New assignment"]}
        header={
          <PanelTitleBlock
            icon={<PersonAddAltOutlined />}
            title={request?.status === "Rejected" ? "Edit & resubmit" : "Assign Salary"}
            chips={request ? <ToneChip label={request.status === "Draft" ? "Draft" : request.number} tone={request.status === "Rejected" ? "red" : "slate"} /> : null}
            subtitle="First-time salary for employees who don't have one yet."
          />
        }
        bodySx={panelBodySx}
        footer={
          <PanelFooter note={`${count} employee${count === 1 ? "" : "s"} · approved on submit`}>
            {request?.status === "Draft" && onDelete ? (
              <SecondaryButton onClick={() => onDelete(request)} disabled={busy} sx={deleteButtonSx}>Delete draft</SecondaryButton>
            ) : null}
            <SecondaryButton onClick={saveDraft} disabled={busy}>Save draft</SecondaryButton>
            <PrimaryButton onClick={submit} disabled={busy}>{request?.status === "Rejected" ? "Resubmit & assign" : "Assign salary"}</PrimaryButton>
          </PanelFooter>
        }
      >
        <Mui.Box sx={{ display: "grid", gridTemplateColumns: expanded ? { xs: "1fr", lg: "minmax(0, 1fr) 400px" } : "1fr", gap: 2, alignItems: "start" }}>
          <Mui.Stack spacing={2} sx={{ minWidth: 0 }}>
            {request?.status === "Rejected" ? (
              <RequestInfoBanner
                tone="danger"
                title={`Rejected at L${request.rejection?.level ?? request.level}${request.rejection?.by ? ` by ${request.rejection.by}` : ""}`}
                body={request.rejection?.reason || "No reason given."}
              />
            ) : null}
            {showErrors && blocking ? <RequestInfoBanner tone="danger" body="Fix the highlighted fields before submitting." /> : null}
            {setup}
            {employeesSection}
          </Mui.Stack>
          <Mui.Box sx={expanded ? { position: { lg: "sticky" }, top: { lg: 0 } } : undefined}>{side}</Mui.Box>
        </Mui.Box>
      </GlobalSidePanel>
      <CustomizeStructurePanel
        fullWidth
        open={Boolean(customizeRow && template && version)}
        catalog={catalog}
        employee={customizeRow ? person(customizeRow.employee_id) : null}
        template={template}
        version={version}
        row={customizeRow}
        onClose={() => setCustomizeId(null)}
        grossFollowsEarnings
        onApply={(lines, gross) => {
          // The row's gross follows the customized Earnings total; a changed
          // gross in "Same gross for all" turns the list to per-employee.
          const newGross = gross > 0 ? String(gross) : customizeRow.gross;
          const grossMoved = String(newGross) !== String(customizeRow.gross);
          set({
            ...(grossMoved && form.gross_mode === "same" ? { gross_mode: "per" } : {}),
            employees: form.employees.map((row) =>
              String(row.employee_id) === String(customizeId) ? { ...row, lines, auto_lines: false, gross: newGross } : row,
            ),
          });
          setCustomizeId(null);
        }}
      />
    </>
  );
};

// ---------------- Review / view ----------------

// The breakdown from the server when it sent one, else worked out from the
// employee's rows (or the template's).
const rowResult = (catalog, templates, request, row) => {
  if (row.breakdown) return row.breakdown;
  const lines = employeeLines(templates, request, row);
  return lines.length && Number(row.gross) > 0 ? evaluateTemplate(catalog, lines, Number(row.gross)) : null;
};

const ChangeBox = ({ catalog, templates, request, row }) => {
  const template = requestTemplate(templates, request);
  const base = requestTemplateVersion(templates, request)?.lines || [];
  const gross = Number(row.gross);
  const changes = structureChanges(catalog, templates, request, row);
  const lines = employeeLines(templates, request, row);
  const custom = rowResult(catalog, templates, request, row);
  const plain = base.length && gross > 0 ? evaluateTemplate(catalog, base, gross) : null;
  const balancing = withBalancing(catalog, lines).find((line) => line.balancing);
  const boxSx = { border: `1px solid ${changes.length ? "#D9D2FB" : designColors.slate200}`, borderRadius: designRadii.card, p: 2, backgroundColor: designColors.white };
  if (!custom) {
    return (
      <Mui.Box sx={boxSx}>
        <Mui.Typography sx={designType.helperText}>The breakdown isn't available for this employee yet.</Mui.Typography>
      </Mui.Box>
    );
  }
  return (
    <Mui.Box sx={boxSx}>
      {changes.length ? (
        <>
          <Mui.Typography sx={{ fontSize: 14, fontWeight: designWeights.semibold, color: "#5B4BD5", mb: 1 }}>
            Change from {request.template_name || template?.name} {versionLabel(request.template_version)}
          </Mui.Typography>
          {changes.map((item) => (
            <Mui.Stack key={item.code} direction="row" justifyContent="space-between" spacing={2} sx={{ py: 0.4 }}>
              <Mui.Typography sx={{ fontSize: 14, color: designColors.slate700 }}>
                <ToneChip label={item.change} tone={item.change === "Added" ? "green" : item.change === "Removed" ? "red" : "amber"} sx={{ mr: 0.75 }} />
                {item.name} · {item.change === "Removed" ? item.before : item.after}
              </Mui.Typography>
              <Mui.Typography sx={{ fontSize: 14, fontWeight: designWeights.semibold }}>{item.change === "Removed" ? "—" : money(custom.values[item.code])}</Mui.Typography>
            </Mui.Stack>
          ))}
          {balancing && plain && plain.values[balancing.code] !== custom.values[balancing.code] ? (
            <Mui.Stack direction="row" justifyContent="space-between" spacing={2} sx={{ py: 0.4 }}>
              <Mui.Typography sx={{ fontSize: 14, color: designColors.slate600 }}>{lineName(catalog, balancing)} adjusts (balancing)</Mui.Typography>
              <Mui.Typography sx={{ fontSize: 14, color: designColors.slate600 }}>{money(plain.values[balancing.code])} → {money(custom.values[balancing.code])}</Mui.Typography>
            </Mui.Stack>
          ) : null}
          <Mui.Stack direction="row" justifyContent="space-between" sx={{ mt: 1, pt: 1, borderTop: `1px dashed ${designColors.slate300}` }}>
            <Mui.Typography sx={{ fontSize: 14, color: designColors.slate700 }}>GROSS {money(custom.earnings)} · Deductions {money(custom.deductions)}</Mui.Typography>
            <Mui.Typography sx={{ fontSize: 14, fontWeight: designWeights.heading }}>NET before statutory {money(custom.net)}</Mui.Typography>
          </Mui.Stack>
        </>
      ) : (
        <BreakdownList catalog={catalog} lines={row.breakdown ? row.breakdown.items : lines} result={custom} />
      )}
    </Mui.Box>
  );
};

export const AssignReviewPanel = ({
  open,
  request,
  catalog,
  templates,
  canApprove,
  loading,
  busy,
  expanded,
  onToggleExpand,
  onClose,
  onApprove,
  onReject,
}) => {
  const trail = useSalaryPanelTrail(SCREEN, onClose, busy);
  const [openRow, setOpenRow] = useState(null);
  const [comment, setComment] = useState("");
  // null or "reject".
  const [mode, setMode] = useState(null);
  const [reason, setReason] = useState("");
  const [showReasonError, setShowReasonError] = useState(false);
  const id = request?.id;
  useEffect(() => {
    setOpenRow(null);
    setComment("");
    setMode(null);
    setReason("");
    setShowReasonError(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  if (!request) return null;

  const template = requestTemplate(templates, request);
  const status = requestStatus(request);
  const count = employeeCount(request);
  const customized = customizedCount(catalog, templates, request);
  const pending = request.status === "Pending";
  const period = PERIOD[request.salary_type] || "month";
  const dotColor = (title) => (title.startsWith("Approved") ? "#12B76A" : title.startsWith("Rejected") || title.startsWith("Cancelled") ? "#DC2626" : title.startsWith("Submitted") || title.startsWith("Resubmitted") || title.startsWith("Imported") ? "#2878FF" : designColors.slate400);
  const checked = request.employees.map((row) => {
    const result = rowResult(catalog, templates, request, row);
    return result ? Math.abs(Number(result.earnings || 0) - Number(row.gross || 0)) < 1 && result.balanced !== false : null;
  }).filter((value) => value !== null);
  const allBalanced = checked.length ? checked.every(Boolean) : null;
  const switchMode = (next) => {
    setMode(next);
    setReason("");
    setShowReasonError(false);
  };
  const danger = { color: "#B42318", borderColor: "#FCA5A5" };

  let footer;
  if (mode === "reject") {
    footer = (
      <PanelFooter note={`Rejecting returns all ${count} employee${count === 1 ? "" : "s"}`}>
        <SecondaryButton onClick={() => switchMode(null)} disabled={busy}>Back</SecondaryButton>
        <PrimaryButton
          sx={dangerButtonSx}
          disabled={busy}
          onClick={() => {
            setShowReasonError(true);
            if (!reason.trim()) return;
            onReject(request, reason.trim());
          }}
        >
          Reject request
        </PrimaryButton>
      </PanelFooter>
    );
  } else if (pending && canApprove) {
    footer = (
      <Mui.Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="space-between" sx={{ width: "100%" }}>
        <Mui.Stack direction="row" spacing={1.5}>
          <SecondaryButton onClick={() => switchMode("reject")} disabled={busy} sx={danger}>Reject</SecondaryButton>
        </Mui.Stack>
        <Mui.Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5}>
          <SecondaryButton onClick={onClose} disabled={busy}>Close</SecondaryButton>
          {canApprove ? (
            <PrimaryButton onClick={() => onApprove(request, comment.trim())} disabled={busy || loading}>
              Approve ({count} employee{count === 1 ? "" : "s"})
            </PrimaryButton>
          ) : null}
        </Mui.Stack>
      </Mui.Stack>
    );
  } else {
    footer = (
      <PanelFooter note={status.detail}>
        <SecondaryButton onClick={onClose}>Close</SecondaryButton>
      </PanelFooter>
    );
  }

  return (
    <GlobalSidePanel
      open={open}
      onClose={busy ? undefined : onClose}
      expanded={expanded}
      onToggleExpand={onToggleExpand}
      breadcrumb={[...trail, request.number]}
      header={
        <PanelTitleBlock
          icon={<AssignmentTurnedInOutlined />}
          title={pending ? "Review salary assignment" : "Salary assignment"}
          chips={<ToneChip label={status.short} tone={status.tone} dot />}
          subtitle={`${request.number} · ${request.type === "Import" ? "Import" : "Assign"} · ${count} employee${count === 1 ? "" : "s"}${request.submitted_at ? ` · submitted ${formatDay(request.submitted_at)}` : ""}`}
        />
      }
      bodySx={panelBodySx}
      footer={footer}
    >
      <Mui.Stack spacing={2}>
        <SummaryStrip
          items={[
            { label: "Template", value: `${request.template_name || template?.name || "–"} · ${versionLabel(request.template_version)}` },
            { label: "Payroll group · cycle", value: `${request.payroll_group || "–"} · ${request.pay_cycle || request.salary_type}` },
            { label: "Employees", value: customized ? `${count} · ${customized} customized` : String(count) },
            { label: `Total gross / ${period}`, value: money(totalGross(request)) },
          ]}
        />
        {request.status === "Rejected" ? (
          <RequestInfoBanner
            tone="danger"
            title={`Rejected at L${request.rejection?.level ?? request.level}${request.rejection?.by ? ` by ${request.rejection.by}` : ""}`}
            body={request.rejection?.reason || "No reason given."}
          />
        ) : null}
        {request.comments ? <RequestInfoBanner title="Comments" body={request.comments} /> : null}
        <SalarySection title="Approval progress">
          <ApprovalSteps request={request} />
        </SalarySection>
        <SalarySection title={`Employees · ${count}`}>
          {loading && !request.details_loaded ? (
            <Mui.Typography sx={designType.helperText}>Loading employees...</Mui.Typography>
          ) : (
            <Mui.Box sx={{ mx: -2, my: -1.75 }}>
              <SimpleTable
                minWidth={600}
                heads={[{ label: "Employee" }, { label: `Gross / ${period}`, align: "right", width: 120 }, { label: "Effective from", width: 130 }, { label: "Structure", width: 170 }, { label: "", width: 100 }]}
              >
                {request.employees.map((row) => {
                  const changes = structureChanges(catalog, templates, request, row).length;
                  const isOpen = openRow === row.employee_id;
                  return (
                    <React.Fragment key={row.employee_id}>
                      <Mui.TableRow sx={{ backgroundColor: isOpen ? designColors.slateBg : undefined }}>
                        <Mui.TableCell sx={tableCellSx}>
                          <Mui.Typography sx={{ fontSize: 14, fontWeight: designWeights.semibold, color: designColors.slate900 }}>{row.name || `Employee ${row.employee_id}`}</Mui.Typography>
                          <Mui.Typography sx={designType.captionText}>{[row.code, row.department].filter(Boolean).join(" · ")}</Mui.Typography>
                        </Mui.TableCell>
                        <Mui.TableCell align="right" sx={{ ...tableCellSx, fontWeight: designWeights.semibold }}>{money(row.gross)}</Mui.TableCell>
                        <Mui.TableCell sx={tableCellSx}>{formatDay(row.effective_from)}</Mui.TableCell>
                        <Mui.TableCell sx={tableCellSx}><StructureChip changes={changes} customized={row.customized} /></Mui.TableCell>
                        <Mui.TableCell align="right" sx={tableCellSx}>
                          <Mui.Link component="button" type="button" underline="hover" onClick={() => setOpenRow(isOpen ? null : row.employee_id)} sx={{ fontSize: 14, fontWeight: designWeights.semibold, color: designColors.tealDark }}>
                            {isOpen ? "Hide" : "Breakdown"}
                          </Mui.Link>
                        </Mui.TableCell>
                      </Mui.TableRow>
                      {isOpen ? (
                        <Mui.TableRow sx={{ backgroundColor: designColors.slateBg }}>
                          <Mui.TableCell colSpan={5} sx={tableCellSx}>
                            <ChangeBox catalog={catalog} templates={templates} request={request} row={row} />
                          </Mui.TableCell>
                        </Mui.TableRow>
                      ) : null}
                    </React.Fragment>
                  );
                })}
                {!request.employees.length ? <EmptyRow colSpan={5} text="No employee details." /> : null}
              </SimpleTable>
            </Mui.Box>
          )}
        </SalarySection>
        {allBalanced !== null ? (
          <CheckLine ok={allBalanced}>
            {allBalanced ? "Earnings add up to gross for every employee" : "Earnings don't add up to gross for every employee"}
          </CheckLine>
        ) : null}
        <SalarySection title="History">
          <Mui.Box sx={{ mx: -2, my: -1.75 }}>
            {request.history.length ? (
              request.history.map((item, index) => (
                <Mui.Stack key={`${item.title}-${index}`} direction="row" spacing={1.5} sx={{ px: 2, py: 1.5, borderTop: index ? `1px solid ${designColors.slate200}` : "none" }}>
                  <Mui.Box sx={{ width: 10, height: 10, mt: 0.75, borderRadius: "50%", backgroundColor: dotColor(item.title), flexShrink: 0 }} />
                  <Mui.Box>
                    <Mui.Typography sx={{ fontSize: 14, color: designColors.slate900 }}><b>{item.title}</b>{item.by ? ` by ${item.by}` : ""}</Mui.Typography>
                    <Mui.Typography sx={designType.captionText}>{formatStamp(item.at)}{item.note ? ` · "${item.note}"` : ""}</Mui.Typography>
                  </Mui.Box>
                </Mui.Stack>
              ))
            ) : (
              <Mui.Typography sx={{ ...designType.helperText, px: 2, py: 1.5 }}>{loading ? "Loading history..." : "No history yet."}</Mui.Typography>
            )}
          </Mui.Box>
        </SalarySection>
        {pending && canApprove && !mode ? (
          <Field label={<>Comment <Mui.Box component="span" sx={{ color: designColors.slate500 }}>· optional</Mui.Box></>}>
            <Mui.TextField size="small" value={comment} onChange={(event) => setComment(event.target.value)} multiline minRows={2} placeholder="Add a note for the history" disabled={busy} sx={multilineFieldSx} />
          </Field>
        ) : null}
        {mode === "reject" ? (
          <SalarySection title="Reject this request" tone="danger">
            <Mui.Typography sx={{ fontSize: 14, color: designColors.slate700, mb: 1.5 }}>
              The whole request ({count} employee{count === 1 ? "" : "s"}) goes back to the submitter as <b>Rejected</b>. Nothing becomes effective.
            </Mui.Typography>
            <Field label={<>Reason<Required /></>} helper="Shown to the submitter and kept in the history" error={showReasonError && !reason.trim() ? "Enter a reason" : undefined}>
              <Mui.TextField size="small" value={reason} onChange={(event) => setReason(event.target.value)} multiline minRows={3} placeholder="Why is this request being rejected?" error={showReasonError && !reason.trim()} disabled={busy} sx={multilineFieldSx} />
            </Field>
          </SalarySection>
        ) : null}
      </Mui.Stack>
    </GlobalSidePanel>
  );
};
